import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";
import { useCommentContext } from "../useComment";
import { TextInput } from "../TextInput";
import { Button, BUTTON_THEME, BUTTON_SIZE } from "../../core/Button";
import {
  COMMENT_BLOCK_TYPE,
  Comment,
} from "@highnote/server/src/core/entities";
import { ErrorBoundary } from "../ErrorBoundary";
import constants from "App/modules/consts";
import { useSpaceCommentsContext } from "../useSpaceComments";
import { useTrack } from "App/components/useEntities/useTrack";
import { getAuthId, useAuth } from "App/components/Auth";
import { useSpaceContext } from "../useSpace";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";
import {
  ConnectedUserAvatar,
  USER_AVATAR_SIZE,
} from "../UserAvatar/UserAvatar";
import "./NewCommentForm.scss";

export const NewCommentForm = ({
  onClose,
  trackId,
  spaceId,
  saveButtonChildren,
  isDisabled,
}: {
  trackId?: Id;
  spaceId: Id;
  onClose?: () => void;
  saveButtonChildren?: React.ReactNode;
  isDisabled?: boolean;
}) => {
  const { user } = useAuth();
  const { space } = useSpaceContext();
  const { comment } = useCommentContext();
  const { addSpaceComment } = useSpaceCommentsContext();
  const { currentTrackVersion } = useTrack();
  const { joinEntity } = useAuth();
  const [text, setText] = useState<string>("");

  const onSave = async () => {
    if (!text) return;

    setText("");

    const commentId = uuidv4();
    const commentData: Comment = {
      id: commentId,
      createdBy: getAuthId(),
      createdAt: Date.now(),
      spaceId,
      trackId: trackId || null,
      trackVersionIds: currentTrackVersion ? [currentTrackVersion.id] : [],
      blocks: [
        {
          type: COMMENT_BLOCK_TYPE.TEXT,
          text: text.trim(),
        },
      ],
    };

    if (comment) {
      commentData.timestamp = comment.timestamp;
      commentData.parentComment = comment.id;
    }

    await addSpaceComment({
      id: commentId,
      data: commentData,
    });

    joinEntity({
      entity: space,
      entityType: "Space",
      trigger: JOIN_ENTITY_TRIGGER.COMMENT,
    });
  };

  return (
    <ErrorBoundary name="NewCommentForm">
      <div
        className={classNames("highnote-new-comment-form", {
          "space-comment-form": !trackId,
        })}
      >
        <TextInput
          value={text}
          onChange={(str) => setText(str)}
          placeholder={"Type here..."}
          onSubmit={onSave}
          isReadOnly={isDisabled}
          maxLength={constants.longMaxLength}
        />

        {onClose && (
          <Button
            className="close"
            size={BUTTON_SIZE.XSMALL}
            theme={BUTTON_THEME.TEXT}
            onClick={onClose}
            disabled={isDisabled}
          >
            Close
          </Button>
        )}
        <Button
          className={classNames("save", { "space-chat-save": !trackId })}
          size={BUTTON_SIZE.XSMALL}
          theme={trackId ? BUTTON_THEME.PRIMARY : BUTTON_THEME.CTA}
          onClick={onSave}
          disabled={isDisabled || !text?.trim()}
          aria-label={trackId ? "Save comment" : "Send comment"}
        >
          {saveButtonChildren || "Send"}
        </Button>
        {!trackId && (
          <div className="space-chat-input-avatar">
            <ConnectedUserAvatar
              userId={user?.id}
              size={USER_AVATAR_SIZE.MEDIUM}
            />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
